import { auth } from '../config/firebase';
import { Slide } from '../types';

const CAROUSEL_API_BASE_URL = process.env.REACT_APP_CAROUSEL_API_BASE_URL || 'https://carousel.dev.carouselshopai.com';

/**
 * Response from POST /api/v1/carousel/search (202 Accepted).
 */
export interface SearchInitiateResponse {
  searchId: string;
  status: string;
}

/**
 * Lean slide reference returned by carousel-svc search results.
 * Contains only the slide ID and shop ID — full Slide data
 * is fetched from slide-svc via batch endpoint.
 */
export interface SearchResultRef {
  id: string;
  shopId: string;
}

/**
 * Pagination metadata from carousel-svc search results.
 */
export interface CarouselPaginationMetadata {
  totalElements: number;
  page: number;
  size: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Search metadata from carousel-svc search results.
 */
export interface CarouselSearchMetadata {
  searchId: string;
  searchType: string;
  prompt: string;
  queryTimeMs: number;
  categoriesResolved: number;
  shopsSearched: number;
  reasoning?: string;
}

/**
 * Full response from GET /api/v1/carousel/search/{searchId} when results are ready.
 */
export interface SearchRefResponse {
  items: SearchResultRef[];
  pagination: CarouselPaginationMetadata;
  metadata: CarouselSearchMetadata;
}

/**
 * Polling response when search is still processing (HTTP 202).
 */
export interface SearchProcessingResponse {
  searchId: string;
  status: 'PROCESSING';
}

/**
 * Failed search response (HTTP 500).
 */
export interface SearchFailedResponse {
  searchId: string;
  status: 'FAILED';
  error: string;
}

/**
 * Recent search item from GET /api/v1/carousel/recent-searches.
 */
export interface RecentSearchItem {
  prompt: string;
  lastUsed: string;
}

export interface RecentSearchResponse {
  items: RecentSearchItem[];
}

/**
 * Feed cube from GET /api/v1/feed.
 */
export interface FeedCube {
  cubeId: string;
  shopIds: string[];
  strategy: string;
  title: string;
  reason: string | null;
  slideRefs: string; // JSON string of [{slideId, shopId}]
  slideCount: number;
  priority: number;
  metadata: string | null;
  generatedAt: string | null;
}

export interface SlideRef {
  slideId: string;
  shopId: string;
}

/**
 * Slide as returned by carousel-svc POST /api/v1/carousel/hydrate. The field set is
 * role-dependent: internal roles get the full set; external roles (CLIENT/VENDOR) get only
 * slideId, shopId, name, description, and published images ({id, displayOrder}). Missing
 * fields are defaulted when mapping to the app's Slide type.
 */
export interface HydratedSlideImage {
  id: string;
  // Not sent for external (CLIENT/VENDOR) roles — images arrive already in display order, so array
  // position IS the order. Reconstructed from the array index when absent (see hydratedToSlide).
  displayOrder?: number;
  filename?: string;
  description?: string;
  uploadedAt?: string;
  published?: boolean;
}

export interface HydratedSlide {
  slideId: string;
  shopId: string;
  name?: string | null;
  // DUAL-KEY (transitional): carousel-svc emits the customer-facing copy under BOTH `description`
  // (legacy) and `marketingDescription` (new). We prefer marketingDescription and fall back to
  // description, so this client keeps working whether it hits an old or new carousel-svc, and the
  // internal working description is never received here.
  description?: string | null;
  marketingDescription?: string | null;
  categories?: string[];
  active?: boolean;
  sold?: boolean;
  published?: boolean;
  locationId?: string | null;
  placeId?: string | null;
  images?: HydratedSlideImage[];
  // External (CLIENT/VENDOR) pricing hint: the most-recent LISTING price only (amount + currency).
  // Never acquisition/sold, never notes/date. Absent when the slide has no LISTING record.
  listingPrice?: number | null;
  listingCurrency?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Map a hydration-response slide to the app's Slide shape (slideId -> id; safe defaults). */
function hydratedToSlide(h: HydratedSlide): Slide {
  return {
    id: h.slideId,
    name: h.name ?? '',
    // Prefer the new marketingDescription key; fall back to the legacy description key (which the
    // server also fills with the customer-facing copy during the dual-key transition). Either way the
    // CLIENT/VENDOR viewer sees the customer-facing copy, labeled "Description" in the UI.
    description: h.marketingDescription ?? h.description ?? '',
    marketingDescription: h.marketingDescription ?? undefined,
    categories: h.categories ?? [],
    shopId: h.shopId,
    images: (h.images ?? []).map((img, idx) => ({
      id: img.id,
      filename: img.filename ?? '',
      description: img.description,
      // External payloads omit displayOrder (images come pre-ordered); fall back to the array index
      // so the app's sort-by-displayOrder rendering keeps the server's order.
      displayOrder: img.displayOrder ?? idx,
      uploadedAt: img.uploadedAt ?? '',
      published: img.published,
    })),
    sold: h.sold ?? false,
    active: h.active,
    published: h.published,
    locationId: h.locationId ?? undefined,
    placeId: h.placeId ?? undefined,
    listingPrice: h.listingPrice ?? undefined,
    listingCurrency: h.listingCurrency ?? undefined,
    createdAt: h.createdAt ?? '',
    updatedAt: h.updatedAt ?? '',
  };
}

export interface FeedPagination {
  page: number;
  size: number;
  totalElements: number;
  hasNext: boolean;
}

export interface FeedResponse {
  cubes: FeedCube[];
  pagination: FeedPagination;
}

class CarouselApiService {
  private async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }

  /**
   * Initiate an async intelligent search.
   * Returns a searchId to poll for results.
   */
  async initiateSearch(prompt: string, shopId: string): Promise<SearchInitiateResponse> {
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/carousel/search`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ prompt, shopId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    return response.json();
  }

  /**
   * Poll for search results.
   * Returns the response status code and body.
   * - 200: results ready (SearchRefResponse)
   * - 202: still processing
   * - 410: expired
   * - 500: failed
   */
  async getSearchResults(searchId: string, page: number = 0, size: number = 20): Promise<{
    status: number;
    data: SearchRefResponse | SearchProcessingResponse | SearchFailedResponse;
  }> {
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/carousel/search/${searchId}?page=${page}&size=${size}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (response.status === 200) {
      const data: SearchRefResponse = await response.json();
      return { status: 200, data };
    }

    if (response.status === 202) {
      const data: SearchProcessingResponse = await response.json();
      return { status: 202, data };
    }

    if (response.status === 410) {
      throw new Error('Search session has expired. Please start a new search.');
    }

    if (response.status === 500) {
      const data: SearchFailedResponse = await response.json();
      throw new Error(`Search failed: ${data.error || 'Unknown error'}`);
    }

    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
  }
  /**
   * Get the user's recent carousel search prompts for a shop.
   */
  async getRecentSearches(shopId: string): Promise<RecentSearchItem[]> {
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/carousel/recent-searches?shopId=${encodeURIComponent(shopId)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      return [];
    }

    const data: RecentSearchResponse = await response.json();
    return data.items;
  }

  /**
   * Get AI-curated feed cubes for the authenticated user.
   */
  async getFeedCubes(page: number = 0, size: number = 10): Promise<FeedResponse> {
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/feed?page=${page}&size=${size}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      return { cubes: [], pagination: { page, size, totalElements: 0, hasNext: false } };
    }

    return await response.json();
  }

  /**
   * Hydrate feed-cube slide refs from carousel-svc (BigQuery-backed, role-aware). Internal
   * roles get full slide data; external roles (CLIENT/VENDOR) get only a whitelist (name,
   * description, published images). Returns Slide[] in input order, dropping slides that are
   * missing or not visible to the caller. Mixed-shop refs are sent in a single call.
   */
  async hydrateSlides(slideRefs: SlideRef[]): Promise<Slide[]> {
    if (slideRefs.length === 0) return [];
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/carousel/hydrate`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ slideRefs }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    const data: { slides: HydratedSlide[] } = await response.json();
    return (data.slides || []).map(hydratedToSlide);
  }

  /**
   * CLIENT Slides-tab browse (AI-Curated Feed Phase 3b, Workstream CE): the caller's cube-visible,
   * PUBLISHED slides for a single shop as a plain, recency-ordered page (carousel-svc GET /slides),
   * role-stripped to the external whitelist. External (CLIENT/VENDOR) members use this in place of
   * slide-svc's slide-data endpoints, which 4b denies them.
   */
  async browseSlides(shopId: string, page: number = 0, size: number = 20): Promise<{ slides: Slide[]; pagination: FeedPagination }> {
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/carousel/slides?shopId=${encodeURIComponent(shopId)}&page=${page}&size=${size}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
    }

    const data: { slides: HydratedSlide[]; pagination: FeedPagination } = await response.json();
    return {
      slides: (data.slides || []).map(hydratedToSlide),
      pagination: data.pagination ?? { page, size, totalElements: 0, hasNext: false },
    };
  }

  /**
   * Dismiss a feed cube for the authenticated user.
   */
  async shopHasFeedCubes(shopId: string): Promise<boolean> {
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/shops/${shopId}/feed-cubes?count=false&size=1`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      return false;
    }

    const data: FeedResponse = await response.json();
    return data.cubes.length > 0;
  }

  async dismissCube(cubeId: string): Promise<void> {
    const token = await this.getAuthToken();
    const url = `${CAROUSEL_API_BASE_URL}/api/v1/feed/dismiss`;

    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ cubeId }),
    });
  }
}

export const carouselApiService = new CarouselApiService();
